<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { CaldavAccount, CaldavCalendar, PriorityTagMode } from '@/api'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { PRIORITY_TAG_MODES, useAccountsEditor } from '@/composables/useAccountsEditor'
import { accountsPath } from '@/settings/accountsNav'

// One calendar: whether it is the default list, how its priority tags work, and the sharing sub page.
const props = defineProps<{ account: CaldavAccount; calendar: CaldavCalendar }>()
const { t } = useI18n()
const editor = useAccountsEditor()

function onMode(event: Event): void {
  void editor.changePriorityMode(props.account, props.calendar, (event.target as HTMLSelectElement).value as PriorityTagMode)
}
</script>

<template>
  <div class="calendar-detail" :data-calendar-id="calendar.id">
    <UiGroup :title="calendar.name">
      <UiList>
        <UiRow as="label">
          <input
            type="checkbox"
            :checked="calendar.is_default"
            data-testid="calendar-default"
            @change="editor.toggleDefault(props.account, calendar)"
          />
          <span class="title">{{ t('accounts.default') }}</span>
        </UiRow>
      </UiList>
      <template #footer>
        <p>{{ t('accounts.defaultHint') }}</p>
      </template>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow as="label" :label="t('accounts.priorityTags')" stack-narrow>
          <select
            class="ui-select"
            :value="calendar.priority_tag_mode"
            data-testid="calendar-priority-mode"
            :aria-label="t('accounts.priorityTags')"
            @change="onMode"
          >
            <option v-for="mode in PRIORITY_TAG_MODES" :key="mode" :value="mode">
              {{ t(`accounts.priorityTagModes.${mode}`) }}
            </option>
          </select>
        </UiRow>
      </UiList>
      <template #footer>
        <p v-if="calendar.priority_tag_mode === 'fine' || calendar.priority_tag_mode === 'both'">
          {{ t('accounts.priorityTagScale') }}
        </p>
        <p>{{ t('accounts.priorityTagsHint') }}</p>
      </template>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow :to="accountsPath(account.id, 'calendars', calendar.id, 'sharing')" class="entry">
          <span class="title">{{ t('accounts.sharing') }}</span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.title {
  flex: 1;
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}
</style>
